import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Brain, TrendingUp, Heart } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function ImpostorSyndromeCoachPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'AI Coach for Impostor Syndrome - Build Confidence | VZMO';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Overcome impostor syndrome with AI coaching. Work through self-doubt, recognize your achievements, and build lasting confidence. Private and judgment-free. Start free.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/coach/impostor-syndrome');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/coach/impostor-syndrome';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('impostor-syndrome-page');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Overcome Impostor Syndrome with AI Coaching
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Feel like a fraud despite your success? Work through self-doubt privately with AI coaching
            designed to help you recognize your achievements and build lasting confidence.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            How VZMO Helps with Impostor Syndrome
          </h2>

          <div className="space-y-6">
            <div className="flex items-start">
              <Brain className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Reframe Your Thinking</h3>
                <p className="text-slate-600">
                  Learn to recognize impostor thoughts as they arise. Work through cognitive distortions
                  and develop healthier self-talk patterns through structured journaling.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Shield className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Document Your Wins</h3>
                <p className="text-slate-600">
                  Keep a private record of your achievements, positive feedback, and growth moments.
                  Build undeniable evidence of your competence over time.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <TrendingUp className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Track Your Progress</h3>
                <p className="text-slate-600">
                  See patterns in when impostor feelings arise. Understand your triggers and develop
                  strategies to manage them proactively.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Heart className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Practice Self-Compassion</h3>
                <p className="text-slate-600">
                  Work through exercises that help you treat yourself with the same kindness you'd show
                  a friend. Build a healthier relationship with yourself.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Signs of Impostor Syndrome</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Attributing success to luck or timing
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Fear of being exposed as a fraud
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Downplaying accomplishments
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Overworking to prove yourself
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Difficulty accepting praise
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Comparing yourself to others
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">What You'll Work On</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Recognizing impostor thoughts
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Challenging negative self-talk
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Accepting compliments gracefully
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Building self-compassion practices
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Celebrating your achievements
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Developing authentic confidence
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <h2 className="text-3xl font-bold mb-4">You Belong Here</h2>
          <p className="text-lg mb-6 opacity-90">
            Start building genuine confidence. Try VZMO free for 7 days.
          </p>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
          <p className="mt-4 text-sm opacity-75">Private coaching, judgment-free space</p>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Common Questions</h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Is impostor syndrome common among successful people?
              </h3>
              <p className="text-slate-600">
                Extremely common. Studies suggest up to 70% of people experience impostor syndrome at some point.
                Many high achievers struggle with it precisely because they set high standards for themselves.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Can AI coaching really help with something psychological like this?
              </h3>
              <p className="text-slate-600">
                Yes. AI coaching provides structured frameworks based on cognitive behavioral techniques. The private,
                judgment-free environment makes it easier to be honest about your thoughts and feelings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                How long does it take to overcome impostor syndrome?
              </h3>
              <p className="text-slate-600">
                It's typically not about complete elimination but building tools to manage it. Most users see
                improvement within weeks of consistent journaling and coaching sessions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
